import React from "react"

import { cn } from "@/domains/library"

type TableProps = React.TableHTMLAttributes<HTMLTableElement>
type TheadProps = React.HTMLAttributes<HTMLTableSectionElement>
type ThProps = React.ThHTMLAttributes<HTMLTableHeaderCellElement>

const Table = React.forwardRef<HTMLTableElement, TableProps>(({ className, children, ...props }, ref) => {
  return (
    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--table-border)" }}>
      <table
        ref={ref}
        className={cn("w-full min-w-full text-sm tabular-nums text-t200", className)}
        style={{ backgroundColor: "var(--table-bg)" }}
        {...props}
      >
        {children}
      </table>
    </div>
  )
})
Table.displayName = "Table"

const TableHead = React.forwardRef<HTMLTableSectionElement, TheadProps>(({ className, children, ...props }, ref) => {
  return (
    <thead ref={ref} className={cn("", className)} style={{ backgroundColor: "var(--table-head-bg)" }} {...props}>
      {children}
    </thead>
  )
})
TableHead.displayName = "TableHead"

const TableHeader = React.forwardRef<
  HTMLTableHeaderCellElement,
  ThProps & { classNameInner?: string; isSortDisabled?: boolean }
>(({ className, classNameInner, isSortDisabled = false, children, ...props }, ref) => {
  return (
    <th
      ref={ref}
      className={cn(
        "content-start px-2 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-t300 sm:px-4",
        !isSortDisabled ? "cursor-pointer hover:text-t100" : "",
        className,
      )}
      {...props}
    >
      <div className={cn("flex items-center gap-1", classNameInner)}>{children}</div>
    </th>
  )
})
TableHeader.displayName = "TableHeader"

const TableHeaderMinimal = React.forwardRef<
  HTMLTableHeaderCellElement,
  ThProps & { classNameInner?: string; isSortDisabled?: boolean }
>(({ className, classNameInner, isSortDisabled = false, children, ...props }, ref) => {
  return (
    <th
      ref={ref}
      className={cn(
        "content-start px-2 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-t300 sm:px-4",
        !isSortDisabled ? "cursor-pointer hover:text-t100" : "",
        className,
      )}
      {...props}
    >
      {children}
    </th>
  )
})
TableHeaderMinimal.displayName = "TableHeaderMinimal"

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, children, ...props }, ref) => (
    <tbody ref={ref} className={cn("table-divide-y", className)} {...props}>
      {children}
    </tbody>
  ),
)
TableBody.displayName = "TableBody"

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, onClick, children, ...props }, ref) => (
    <tr ref={ref} className={cn("group table-row", className)} onClick={onClick} {...props}>
      {children}
    </tr>
  ),
)
TableRow.displayName = "TableRow"

const TableHeaderRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, children, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("group table-row", className)}
      style={{
        backgroundColor: "var(--table-head-bg)",
      }}
      {...props}
    >
      {children}
    </tr>
  ),
)
TableHeaderRow.displayName = "TableHeaderRow"

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, children, ...props }, ref) => (
    <td ref={ref} className={cn("table-cell-hover table-cell px-2 py-2.5 sm:px-4", className)} {...props}>
      {children}
    </td>
  ),
)
TableCell.displayName = "TableCell"

const Compound = {
  Root: Table,
  Head: TableHead,
  Th: TableHeader,
  thMinimal: TableHeaderMinimal,
  Body: TableBody,
  Tr: TableRow,
  HeaderRow: TableHeaderRow,
  Td: TableCell,
}

export { Compound, Table, TableHead, TableHeader, TableHeaderMinimal, TableBody, TableRow, TableHeaderRow, TableCell }
